import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Redirect, Link } from 'react-router-dom';
import classNames from 'classnames';
import ClipLoader from 'react-spinners/ClipLoader';

import { useFormatMessage, useFormatDate } from 'hooks';
import Table from 'components/Table';
import { fetchVendors, deleteVendor, vendorsCleanUp } from 'state/actions/vendors';
import { fetchCountries } from 'state/actions/countries';
import paths from 'pages/Router/paths';
import ConfirmationModal from 'components/ConfirmationModal';
import classes from './Users.module.scss';

const Vendors = () => {
  const { vendorsList, isAdmin, error, loading, deleted, countriesList } = useSelector(
    (state) => ({
      vendorsList: state.vendors.data,
      isAdmin: state.auth.userData.isAdmin,
      error: state.vendors.error,
      loading: state.vendors.loading,
      deleted: state.vendors.deleted,
      countriesList: state.countries.data,
    }),
    shallowEqual
  );

  const [deleteModal, setDeleteModal] = useState({
    userId: null,
    isOpen: false,
  });

  const dispatch = useDispatch();

  const [search, setSearch] = useState('');

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchVendors());
      dispatch(fetchCountries());
    }

    return () => dispatch(vendorsCleanUp());
  }, [dispatch, isAdmin]);

  useEffect(() => {
    if (deleted && !loading) {
      setDeleteModal((prevState) => ({
        userId: null,
        isOpen: !prevState.isOpen,
      }));
    }
  }, [deleted, loading]);

  const redirect = !isAdmin && <Redirect to={paths.ROOT} />;

  const onRemoveButtonClickHandler = (userId) => {
    setDeleteModal((prevState) => ({
      userId,
      isOpen: !prevState.isOpen,
    }));
  };

  const onCloseModalHandler = () => {
    setDeleteModal({ userId: null, isOpen: false });
  };

  const onDeleteVendorHandler = () => {
    dispatch(deleteVendor(deleteModal.userId));
  };

  const columns = [
    {
      Header: '',
      accessor: 'logoUrl',
      Cell: ({ row }) => (
        <div className="image">
          <img src={row.original.logoUrl} alt="" />
        </div>
      ),
      disableSortBy: true,
    },
    {
      Header: "Name",
      accessor: 'name',
    },
    {
      Header: "Card",
      accessor: 'card',
    },
    {
      Header: "Priority",
      accessor: 'priority',
    },
    {
      Header: 'Countries',
      accessor: 'countries',
      Cell: ({ row }) => {
        const vendorCountries = row.original.countries || [];
        if (vendorCountries.length === 0) return <small className="has-text-grey is-abbr-like">All</small>;
        const names = vendorCountries
          .map((cId) => {
            const found = countriesList.find((c) => c.id === cId);
            return found ? found.name : cId;
          })
          .join(', ');
        return <small className="has-text-grey is-abbr-like">{names}</small>;
      },
    },
    {
      Header: "Active Coupon",
      accessor: 'activeCoupon',
      Cell: ({ row }) => (
        <small className="has-text-grey is-abbr-like">
          {row.original.activeCoupon === 1 ? (
            <span className="icon">
              <i className="mdi mdi-check" />
            </span>
          ) : (
            <span className="icon">
              <i className="mdi mdi-close" />
            </span>
          )}
        </small>
      ),
    },
    {
      Header: "Active Catalogue",
      accessor: 'activeCatalogue',
      Cell: ({ row }) => (
        <small className="has-text-grey is-abbr-like">
          {row.original.activeCatalogue === 1 ? (
            
            <span className="icon">
              <i className="mdi mdi-check" />
            </span>
          ) : (
            <span className="icon">
              <i className="mdi mdi-close" />
            </span>
          )}
        </small>
      ),
    },
    {
      Header: 'Loyalty Card',
      accessor: 'loyaltyCard',
      Cell: ({ row }) => (
        <small className="has-text-grey is-abbr-like">
          {row.original.loyaltyCard ? (
            <span className="icon">
              <i className="mdi mdi-check" />
            </span>
          ) : (
            <span className="icon">
              <i className="mdi mdi-close" />
            </span>
          )}
        </small>
      ),
    },
    {
      Header: "Last Modified",
      accessor: 'created',
      Cell: ({ row }) => (
        <small className="has-text-grey is-abbr-like">
          {useFormatDate(row.original.createdAt, {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </small>
      ),
    },
    {
      Header: '',
      id: 'actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <>
          {!row.original.isAdmin && (
            <div className="buttons is-right">
              <Link
                to={`/vendors/${row.original.id}`}
                className="button is-small is-primary"
              >
                <span className="icon is-small">
                  <i className="mdi mdi-account-edit" />
                </span>
              </Link>

              <button
                type="button"
                className="button is-small is-danger"
                onClick={() => onRemoveButtonClickHandler(row.original.id)}
              >
                <span className="icon is-small">
                  <i className="mdi mdi-trash-can" />
                </span>
              </button>
            </div>
          )}
        </>
      ),
      disableSortBy: true,
    },
  ];

  const data = search
    ? vendorsList.filter((el) => {
        return el?.name.toLowerCase().includes(search.toLowerCase());
      })
    : vendorsList;

  const deleteMessage = useFormatMessage('Users.delete');

  const confirmMessage = useFormatMessage('Users.confirm');

  const permDeleteMessage = useFormatMessage('Users.permDelete');

  const cancelMessage = useFormatMessage('Users.cancel');

  return (
    <>
      {redirect}
      {deleteModal.isOpen && (
        <ConfirmationModal
          isActive={deleteModal.isOpen}
          isLoading={loading}
          confirmButtonMessage={deleteMessage}
          title={confirmMessage}
          body={permDeleteMessage}
          cancelButtonMessage={cancelMessage}
          onConfirmation={onDeleteVendorHandler}
          onCancel={onCloseModalHandler}
        />
      )}
      <section className="hero is-hero-bar">
        <div className="hero-body">
          <div className="level">
            <div className="level-left">
              <div className="level-item">
                <h1 className="title">Vendors</h1>
              </div>
            </div>
            <div className="level-right">
              <div className="level-item">
                <Link to={paths.ADD_VENDOR} className="button">
                  New Vendor
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="section is-main-section">
        <div className="card has-table has-mobile-sort-spaced">
          <header className="card-header">
            <p className={classNames('card-header-title', classes.tableHeader)}>
              <span>{useFormatMessage('Users.search')}</span>
              <input
                type="text"
                className="input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </p>
          </header>
          <div className="b-table">
            {loading ? <ClipLoader /> : <Table columns={columns} data={data} />}
            {error && 'Show error'}
          </div>
        </div>
      </section>
    </>
  );
};

export default Vendors;

